import React from 'react';

import { useCrossProductUrlWrapper } from '@atlassiansox/analytics-cross-product-interaction-client';
import { useIntl } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import CrossIcon from '@atlaskit/icon/core/cross';
import { fg } from '@atlaskit/platform-feature-flags';
import { Flex, Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import messages from '../../common/messages';
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import { PreviewPanelNewTabLink } from '../preview-panel-action-variants/preview-panel-new-tab-link';

import type { PreviewPanelHeaderProps } from './types';

/**
 * Removes the specified query parameter from a URL while preserving all other parameters
 * @param url - The original URL
 * @param paramToRemove - The query parameter name to remove
 * @returns The URL with the specified parameter removed
 */
export const removeQueryParam = (url: string, paramToRemove: string): string => {
	try {
		const urlObj = new URL(url);
		urlObj.searchParams.delete(paramToRemove);
		return urlObj.toString();
	} catch {
		// If URL parsing fails, return the original URL
		return url;
	}
};

const styles = cssMap({
	header: {
		paddingInlineStart: token('space.300'),
		paddingInlineEnd: token('space.300'),
		paddingBlockStart: token('space.200'),
		paddingBlockEnd: token('space.200'),
		borderBlockEndStyle: 'solid',
		borderBlockEndWidth: token('border.width'),
		borderColor: token('color.border'),
		height: '56px', //there's no token for this height :(
	},
	childWrapper: {
		flexGrow: 1,
	},
});

export const PreviewPanelHeader = ({
	children,
	onClose,
	onOpenInNewTab,
	testId,
	url,
	analyticsClient,
	parentProduct,
	panelType,
	moreActions,
}: PreviewPanelHeaderProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const enableCrossMAUUsage = fg('platform_preview_panels_cross_app_interaction');

	const buildUrlWithAnalyticsParams = useCrossProductUrlWrapper({
		bridge: 'preview-panels',
		product: parentProduct ?? 'unknown',
	});

	const onPanelClose = () => {
		analyticsClient?.sendTrackEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'preview-panel-close',
			source: 'preview-panel',
			attributes: {
				parentProduct,
				panelType,
			},
		});
		onClose?.();
	};

	const onOpenInNewWindow = () => {
		analyticsClient?.sendTrackEvent({
			action: 'clicked',
			actionSubject: 'link',
			actionSubjectId: 'preview-panel-open-in-new-window',
			source: 'preview-panel',
			attributes: {
				parentProduct,
				panelType,
			},
		});
		onOpenInNewTab?.();
		if (url) {
			window.open(
				enableCrossMAUUsage
					? buildUrlWithAnalyticsParams(removeQueryParam(url, 'previewPanels'))
					: removeQueryParam(url, 'previewPanels'),
			);
		}
	};

	return (
		<Inline xcss={styles.header} testId={testId} alignBlock="center" space="space.100">
			<Inline
				xcss={styles.childWrapper}
				spread="space-between"
				alignBlock="center"
				space="space.100"
			>
				{children}
			</Inline>
			<Flex gap="space.100">
				{url && (
					<PreviewPanelNewTabLink
						label={formatMessage(messages.previewPanelOpenInNewWindowLabel)}
						onClick={onOpenInNewWindow}
						testId={testId && `${testId}.open-in-new-window-button`}
					/>
				)}
				{moreActions}
				{onClose && (
					<IconButton
						icon={(iconProps) => <CrossIcon {...iconProps} size="small" />}
						label={formatMessage(messages.previewPanelCloseButtonLabel)}
						onClick={onPanelClose}
						testId={testId && `${testId}.close-button`}
						role="button"
					/>
				)}
			</Flex>
		</Inline>
	);
};
